import * as React from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Form,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import DurationInput from 'modules/core/components/DurationInput'
import Select from 'modules/core/components/Select'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useUpdateAttendanceSetting from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateAttendanceSetting'
import AttendanceSettingForm from 'modules/attendance/lib/forms/AttendanceSettingForm'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import { ForgotClockOutOption } from 'modules/attendance/store/models/AttendanceSetting'

const { CLOCK_OUT_REMINDER, DISABLED, MISSING_CLOCK_OUT } = ForgotClockOutOption

const forgotClockOutOptions = () => {
  const options = Object.values(ForgotClockOutOption)
  const filteredOptions = options.filter((option) => option !== DISABLED)

  return filteredOptions.map((option) => {
    if (option === CLOCK_OUT_REMINDER) {
      return {
        label: i18n.t('time_tracking.settings.inconsistencies.options.confirm_clock_out.label'),
        value: option,
      }
    } else {
      return {
        label: i18n.t('time_tracking.settings.inconsistencies.options.open_shift.label'),
        value: option,
      }
    }
  })
}

const trackToggleClockOutRestriction = (option: string) => {
  track(trackingEvents.timeTracking.toggleClockOutRestrictions, {
    toggle_enabled: option !== DISABLED,
    option_selected:
      option === CLOCK_OUT_REMINDER
        ? 'Request clock out confirmation'
        : 'Maintain shift as unresolved',
  })
}

interface Props {
  attendanceSetting: AttendanceSetting
}

const createSettingForm = (setting: AttendanceSetting) =>
  new AttendanceSettingForm({
    forgot_clock_out: setting.attributes.forgot_clock_out,
    forgot_clock_out_tolerance_minutes: setting.attributes.forgot_clock_out_tolerance_minutes,
  })

const MissingClockOut = ({ attendanceSetting }: Props) => {
  const [settingForm, setSettingForm] = React.useState(createSettingForm(attendanceSetting))
  const forgotClockOutField = settingForm.get('forgot_clock_out')
  const toleranceMinutesField = settingForm.get('forgot_clock_out_tolerance_minutes')

  const { updateSetting } = useUpdateAttendanceSetting(settingForm, attendanceSetting)
  const [toggle, setToggle] = React.useState(attendanceSetting.forgotClockOutEnabled)

  useEffect(() => {
    setSettingForm(createSettingForm(attendanceSetting))
    setToggle(attendanceSetting.forgotClockOutEnabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceSetting.attributes.id])

  const onChange = (option: string) => {
    forgotClockOutField.set(option)
    trackToggleClockOutRestriction(option)
    if (option !== MISSING_CLOCK_OUT) updateSetting()
  }

  const enableToggle = () => {
    setToggle((prevValue) => {
      const newValue = !prevValue
      newValue ? onChange(CLOCK_OUT_REMINDER) : onChange(DISABLED)
      return newValue
    })
  }

  const forgotClockOutValue = forgotClockOutField.value
  const optionDescription =
    forgotClockOutValue === CLOCK_OUT_REMINDER
      ? i18n.t('time_tracking.settings.inconsistencies.options.confirm_clock_out.description')
      : i18n.t('time_tracking.settings.inconsistencies.options.open_shift.description')

  const hasMissingClockout = forgotClockOutValue === MISSING_CLOCK_OUT
  const submitButton = settingForm.isDirty && hasMissingClockout

  return (
    <CollapsableCard
      open={toggle}
      Card={
        <Box gap="s8">
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal weight="medium">
              {i18n.t('time_tracking.settings.inconsistencies.action')}
            </Text.Normal>
            <TogglerCheckbox value={toggle} onChange={() => enableToggle()} labelBefore />
          </Box>
          <Text.Normal>{i18n.t('time_tracking.settings.inconsistencies.description')}</Text.Normal>
        </Box>
      }
      Content={
        <Form onSubmit={() => updateSetting()} padding={null}>
          <Box gap="s16">
            <Select
              value={forgotClockOutValue}
              onChange={(value: string) => onChange(value)}
              options={forgotClockOutOptions()}
              extra={{ 'data-intercom-target': 'attendance-timesheet-missing-clock-out-select' }}
            />
            {hasMissingClockout && (
              <Box width="full">
                <DurationInput
                  label={i18n.t('time_tracking.settings.inconsistencies.threshold_time')}
                  minutes={toleranceMinutesField.value}
                  onChange={(value: number) => toleranceMinutesField.set(value)}
                />
              </Box>
            )}
            <Box
              borderRadius={{
                all: 'abs008',
              }}
              background="grey400"
              justifyContent="center"
              alignItems="center"
              padding="s16"
            >
              <Text.Normal>{optionDescription}</Text.Normal>
            </Box>
            {forgotClockOutValue === CLOCK_OUT_REMINDER && (
              <Text.Normal size="200">
                {i18n.t(
                  'time_tracking.settings.inconsistencies.options.confirm_clock_out.disclaimer'
                )}
              </Text.Normal>
            )}
            {submitButton && (
              <Box alignItems="flexEnd">
                <Button type="primary" submit>
                  {i18n.t('common.submit')}
                </Button>
              </Box>
            )}
          </Box>
        </Form>
      }
    />
  )
}

export default observer(MissingClockOut)
